import type { LocalizedString } from '@/services/dto';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppUtils } from '@/utils/app';
import { getFloorDisplayLabel } from '@/utils/floor-guide';

type Props = {
    floorName?: LocalizedString;
    categoryOnFloor?: LocalizedString;
    isShowAllFloorGuide: boolean;
};

const FAKE_FLOOR_NAME: LocalizedString = {
    ko: '1F',
    en: '1F',
    ja: '1F',
    zh: '1F',
};

const FAKE_CATEGORY_ON_FLOOR: LocalizedString = {
    ko: '패션 / 뷰티 / 라이프스타일',
    en: 'Fashion / Beauty / Lifestyle',
    ja: 'ファッション / ビューティー / ライフスタイル',
    zh: '时尚 / 美妆 / 生活方式',
};

export const VerticalMapHeader = ({ floorName, categoryOnFloor, isShowAllFloorGuide }: Props) => {
    const { i18n, t } = useTranslation();
    const actualFloorName = floorName ?? FAKE_FLOOR_NAME;
    const actualCategoryOnFloor = categoryOnFloor ?? FAKE_CATEGORY_ON_FLOOR;

    const displayText = useMemo(() => {
        if (!actualCategoryOnFloor) {
            return '';
        }
        return actualCategoryOnFloor[i18n.language] ?? '';
    }, [actualCategoryOnFloor, i18n]);

    const floorNameText = useMemo(() => {
        if (!actualFloorName) {
            return '';
        }
        return actualFloorName[i18n.language] ?? actualFloorName?.['ko'] ?? '';
    }, [actualFloorName, i18n.language]);

    const floorDisplayText = useMemo(() => {
        if (!floorNameText) {
            return '';
        }
        return getFloorDisplayLabel(floorNameText);
    }, [floorNameText]);

    return (
        <section className="bg-[var(--color-bg-white-to-bg-basic-black)] fullscreen:h-[calc(212*var(--vh-unit))] h-[calc(212*var(--vh-unit))] mt-[calc(-17*var(--vh-unit))] w-full border-b border-solid border-[var(--text-black-02)] contrast:border-not-contrast-white pl-[calc(56*var(--vh-unit))]">
            {!isShowAllFloorGuide ? (
                <div className="mt-[calc(90*var(--vh-unit))] flex items-center justify-start gap-[calc(20*var(--vh-unit))]">
                    {floorDisplayText && <p
                        data-region-nav
                        data-region-order={1}
                        className="text-text-main01 text-[calc(96*var(--vh-unit))] leading-[calc(100*var(--vh-unit))] focus-border "
                        tabIndex={1}
                        data-interactive
                        aria-label={t('floor_guide.text_to_speech.floor', { floorName: AppUtils.getFloorTTSText(floorNameText) })}
                    >
                        {floorDisplayText}
                    </p>}
                    {displayText && <p
                        data-region-nav
                        data-region-order={2}
                        className="text-text-main01 text-[calc(28*var(--vh-unit))] leading-[calc(34*var(--vh-unit))] focus-border "
                        tabIndex={2}
                        data-interactive
                        aria-label={displayText}
                    >
                        {displayText || <span>&nbsp;</span>}
                    </p>}
                </div>
            ) : (
                <p tabIndex={1} data-interactive aria-label={t('floor_guide.label.all_floors_guide')} className="text-text-main01 mt-[calc(86*var(--vh-unit))] text-center text-[calc(60*var(--vh-unit))] leading-[calc(90*var(--vh-unit))] font-bold focus-border all-floor-guide-title">
                    {t('floor_guide.label.all_floors_guide')}
                </p>
            )}
        </section>
    );
};
